import moment, { max } from 'moment';
import uuid from 'react-uuid';

export function generateDaysWithEvents(
  start,
  period,
  events = [],
  calendars,
  userId
) {
  const days = [];
  let firstCalendarDay;
  let lastCalendarDay;
  let current;

  // Generate calendar grid (month or week)
  if (period === 'month') {
    const startDate = moment(start).startOf('month');
    const endDate = moment(start).endOf('month');

    // Extend calendar to full weeks (start on Monday, end on Sunday)
    firstCalendarDay = startDate.clone().startOf('isoWeek');
    lastCalendarDay = endDate.clone().endOf('isoWeek');

    current = firstCalendarDay.clone();
    while (current.isSameOrBefore(lastCalendarDay, 'day')) {
      days.push({
        id: uuid(),
        date: current.clone(),
        events: [],
        wholeDayEvents: [],
        isCurrentInterval:
          current.isSameOrAfter(startDate) && current.isSameOrBefore(endDate),
      });
      current.add(1, 'day');
    }
  } else if (period === 'week') {
    const startDate = moment(start);
    firstCalendarDay = startDate.clone().startOf('isoWeek');
    lastCalendarDay = firstCalendarDay.clone().add(6, 'days');

    current = firstCalendarDay.clone();
    while (current.isSameOrBefore(lastCalendarDay, 'day')) {
      days.push({
        id: uuid(),
        date: current.clone(),
        events: [],
        wholeDayEvents: [],
        isCurrentInterval: true,
      });
      current.add(1, 'day');
    }
  } else if (period === 'day') {
    const dayDate = moment(start).startOf('day');

    days.push({
      id: uuid(),
      date: dayDate.clone(),
      events: [],
      wholeDayEvents: [],
      isCurrentInterval: true,
    });
  }

  // Group days by ISO week
  const weeks = [];
  days.forEach((day) => {
    const isoWeek = day.date.isoWeek();
    let week = weeks.find((w) => w.week === isoWeek);
    if (!week) {
      week = { week: isoWeek, days: [] };
      weeks.push(week);
    }
    week.days.push(day);
  });

  // Sort and filter events (first by duration, then by start time)
  const sortedEventsByLength = [...events].sort(
    (a, b) =>
      moment(a.startDateTime).diff(moment(a.endDateTime)) -
        moment(b.startDateTime).diff(moment(b.endDateTime)) || a.id - b.id
  );

  const sortedEvents = [...sortedEventsByLength].sort(
    (a, b) =>
      moment(a.startDateTime).valueOf() - moment(b.startDateTime).valueOf() ||
      a.id - b.id
  );
  // Separate whole-day and normal events
  const wholeDayEventsList = sortedEvents.filter((e) => e.wholeDay);
  const normalEventsList = sortedEvents.filter((e) => !e.wholeDay);

  // Process each week separately for isolated layer stacking
  weeks.forEach((week) => {
    const weekDays = week.days;
    // WHOLE-DAY EVENTS
    const weekWholeDayEvents = wholeDayEventsList.filter((event) => {
      const start = moment(event.startDateTime);
      const end = moment(event.endDateTime);
      return weekDays.some((day) =>
        day.date.isBetween(start, end, 'day', '[]')
      );
    });

    // 🧹 Clean up empty whole-day layers
    week.days.forEach((day) => {
      const usedWholeDayLayers = new Set(
        day.events.filter((e) => e.wholeDay).map((e) => e.layer)
      );

      // If there are gaps (e.g., only layer 1 is used, but layer 0 is empty),
      // shift layers downward to remove visual gaps
      const sortedUsedLayers = [...usedWholeDayLayers].sort((a, b) => a - b);

      sortedUsedLayers.forEach((originalLayer, newLayerIndex) => {
        if (originalLayer !== newLayerIndex) {
          // Update event layers
          day.events.forEach((e) => {
            if (e.wholeDay && e.layer === originalLayer) {
              e.layer = newLayerIndex;
            }
          });
        }
      });
    });

    let maxWholeDayLayer = 0;

    //Assign whole-day events with minimal available layer per week
    weekWholeDayEvents.forEach((event) => {
      const eventStart = moment(event.startDateTime);
      const eventEnd = moment(event.endDateTime);

      const eventDays = weekDays.filter((day) =>
        day.date.isBetween(eventStart, eventEnd, 'day', '[]')
      );

      // Find first available layer that is not occupied on any of the event days
      let layer = 0;
      while (
        eventDays.some((day) =>
          day.events.some((e) => e.wholeDay && e.layer === layer)
        )
      ) {
        layer++;
      }

      //update max layer for whole-day events
      maxWholeDayLayer = Math.max(maxWholeDayLayer, layer);

      // Add event to each day it spans
      eventDays.forEach((targetDay) => {
        // Create a new event object with necessary properties
        const newEvent = {
          ...event,
          isFirst: targetDay.date.isSame(eventStart, 'day'),
          isLast: targetDay.date.isSame(eventEnd, 'day'),
          isFirstInWeek: targetDay.date.isoWeekday() === 1,
          layer: layer,
        };

        const userCalendarIds = calendars?.map((cal) => cal.id) || [];
        const ownerMatch = String(newEvent.ownerId) === String(userId);
        const userCalendarMatch = userCalendarIds.includes(
          newEvent.userCalendar?.id
        );

        const calendarMatch = userCalendarIds.includes(newEvent.calendar?.id);

        const eventAlreadyExists = targetDay.events.some(
          (e) => e.id === newEvent.id
        );

        if (
          (ownerMatch || userCalendarMatch || calendarMatch) &&
          !eventAlreadyExists
        ) {
          targetDay.events.push(newEvent);
        }
      });
    });

    // console.log(maxWholeDayLayer);

    // NORMAL EVENTS
    const weekNormalEvents = normalEventsList.filter((event) => {
      const start = moment(event.startDateTime);
      const end = moment(event.endDateTime);
      return weekDays.some((day) =>
        day.date.isBetween(start, end, 'day', '[]')
      );
    });

    // Assign normal events strating from max whole-day layers
    weekNormalEvents.forEach((event) => {
      const eventStart = moment(event.startDateTime);
      const eventEnd = moment(event.endDateTime);

      const eventDays = weekDays.filter((day) =>
        day.date.isBetween(eventStart, eventEnd, 'day', '[]')
      );

      let layer = maxWholeDayLayer + 1;
      while (
        eventDays.some((day) =>
          day.events.some((e) => !e.wholeDay && e.layer === layer)
        )
      ) {
        layer++;
      }

      eventDays.forEach((targetDay) => {
        const newEvent = {
          ...event,
          isFirst: targetDay.date.isSame(eventStart, 'day'),
          isLast: targetDay.date.isSame(eventEnd, 'day'),
          isFirstInWeek: targetDay.date.isoWeekday() === 1,
          layer: layer,
        };

        const userCalendarIds = calendars?.map((cal) => cal.id) || [];
        const ownerMatch = String(newEvent.ownerId) === String(userId);
        const userCalendarMatch = userCalendarIds.includes(
          newEvent.userCalendar?.id
        );
        //case for invited events
        const calendarMatch = userCalendarIds.includes(newEvent.calendar?.id);

        const eventAlreadyExists = targetDay.events.some(
          (e) => e.id === newEvent.id
        );

        if (
          (ownerMatch || userCalendarMatch || calendarMatch) &&
          !eventAlreadyExists
        ) {
          targetDay.events.push(newEvent);
        }
      });
    });
  });

  return days;
}
